/**
 * 「这一行能不能退款」的判据 —— **唯一实现**（`DR-066` §5 · `AC-B5.12`）。
 *
 * ## 🔴 为什么抽出来
 *
 * 修前这条判据只写在 `HomePage` 的 JSX 里（`t.parent.kind !== Transfer && net < 0`），
 * 于是「流水页也要能退款」（负责人 2026-10-28 第 6 条）时，只能在第二处再写一遍 ——
 * 那是同一事实的两份实现（铁律 24），而且两份迟早会分叉（一处补了 `parentId` 判断、
 * 另一处没补），表现是"首页能退、流水页点了报 400"。
 *
 * ## 判据（三条**同时**成立）
 *
 * | 条件 | 含义 |
 * |---|---|
 * | `parent.kind !== Transfer` | 转账是资金搬运，没有"被冲减的消费"；服务端 `checkRefundParent` 也会拦 |
 * | `(parent.net ?? 0) < 0` | **净流出**才是支出（工资条那种多笔"各腿相加"没有语义，只有 `net` 有定义） |
 * | `parentId === null` | 本笔自己**不是退款** —— 退款不能再被退，否则会形成冲减链 |
 *
 * 🔴 全部读**服务端派生好的字段**（`parent.*` / `parentId`），端侧不自己按腿判形态。
 * 🔴 不按 `single` / `multi` 筛：一笔**拆分开的支出**同样可能整笔被退 ——
 * 退款冲的是"这笔消费"，不是某一条腿。
 */

import { LegDirection, ParentRowKind, type TransactionRowView } from '@jiaying/shared';

/** 是否需要 `canRefund` 判定所需的最小投影 */
export type RefundCandidate = Pick<TransactionRowView, 'parentId' | 'parent' | 'refunds'>;

/** 这一行能不能发起退款（`DR-066` §5 · `AC-B5.12`）。唯一实现。 */
export function canRefund(row: RefundCandidate): boolean {
  // ① 转账不是消费
  if (row.parent.kind === ParentRowKind.Transfer) return false;
  // ② 必须是净流出（净额为 0 或为正 = 不是支出）
  if ((row.parent.net ?? 0) >= 0) return false;
  // ③ 本笔不能自己就是退款（防冲减链）
  if (row.parentId !== null) return false;
  /**
   * ④ 🆕 批 `120`（`DR-069` D6）：**已经全额退完的不再给退款入口**。
   *
   * 修前只判"净流出"，于是一笔 175.50 已退清的行仍然挂着「退款」按钮 ——
   * 点进去填任何金额都是二次退款（支出分类会被冲成负、或者被算成收入）。
   * 判据用**原额绝对值**（`-net`），不用 `refundHint` 的文案。
   *
   * ⚠️ `?? null` 不是多余：本函数还会吃到**旧响应**（缓存里没有 `refunds` 键）与
   * 手写夹具 —— `undefined !== null` 会让下面的 `.amount` 直接抛（本批实测踩到）。
   */
  const refunded = row.refunds ?? null;
  if (refunded !== null && refunded.amount >= -(row.parent.net ?? 0)) return false;
  return true;
}

/**
 * 还能退多少（`DR-069` D6）—— 退款页「最多可退」的唯一来源。
 *
 * `原额 − 已退`，负数一律归 0（数据异常时不显示负的"最多可退"）。
 */
export function refundableAmount(row: RefundCandidate): number {
  const net = row.parent.net ?? 0;
  if (net >= 0) return 0;
  const left = -net - (row.refunds?.amount ?? 0);
  return left > 0 ? left : 0;
}

/**
 * 退款草稿要预填的**原支出分类** —— 唯一实现（批 `119`）。
 *
 * ## 🔴 为什么退款腿必须挂**支出**分类（这是本函数存在的全部理由）
 *
 * 退款的形态判据在内核 `isRefundTxn()`（`nucleus/split.ts`）：
 *
 * ```
 * 计入收支的腿全部是 in  +  至少一条挂**支出**分类  ⇒  才算退款
 * ```
 *
 * 挂**收入**分类的 `in` 腿会被判成"一笔普通收入" ⇒ 服务端 `bindRefundToParent()`
 * 直接 400（`parentId 只能用于退款（in 腿 + 挂原支出分类）`）。
 *
 * ⚠️ 修前端侧没有这个意识：退款表单方向锁 `in`、分类选择器按方向筛成**收入分类**
 * ⇒ 原支出分类在列表里根本不存在，用户只能不选或选一个收入分类，**两条路都 400**
 * （负责人 2026-10-29 反馈「退款无法退款…收入没有原来支出的分类，故报错」）。
 *
 * ## 取值口径
 *
 * 退款冲的是**整笔消费**（不是某一条腿）⇒ 多腿拆分时可能有好几个分类。
 * 这里取**第一条挂了分类的支出腿**作为预填**建议值**（用户在表单里仍可改）——
 * 不做"拆分退款要按比例分"的推断：那会让一笔退款变成多腿，而退款只允许单腿。
 */
export function refundCategoryId(row: Pick<TransactionRowView, 'legs'>): string | null {
  const hit = row.legs.find(
    (l) => l.direction === LegDirection.Out && l.categoryId !== null,
  );
  return hit?.categoryId ?? null;
}
